import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Footer from '../components/Footer';
import BrandedLoading from '../components/BrandedLoading';
import { sb } from '../supabase';
import { attachSupplierProfiles } from '../lib/profileVisibility';
import { formatPriceLocale } from '../lib/formatLocale';
import { offerValidityText, offerSampleText } from '../lib/offerValidity';
import { deliveryLine } from '../lib/priceBasisExplainer';
// Status LABELS, not raw column values. requestStatusLabel is the one map for
// requests.status (src/lib/requestStatus.js owns that vocabulary); OFFER_STATUS
// is the existing trilingual map for offers.status. Neither is redefined here.
import { requestStatusLabel } from '../lib/requestStatus';
import { REQUEST_UNITS, labelFor } from '../lib/requestFormOptions';
import { OFFER_STATUS } from '../lib/supplierDashboardConstants';
import { fetchOfferAmounts, formatSar } from '../lib/paymentQuote';

// /requests/:id — the button on request_received, offer_received and
// offer_accepted.
//
// LOGIN-GATED, unlike /track/:token. It carries requests.id and shows the
// request with its offers, so RLS is what decides who sees it: the buyer who
// owns the request, a supplier with standing on it, or an admin. This page adds
// no access logic of its own — an empty read means "not yours", and it says so
// without confirming whether the id exists.
//
// READ-ONLY, deliberately. Accepting or rejecting an offer writes `offers` and
// `requests`, inserts notifications and sends email (DashboardBuyer.jsx:851-930).
// Forking that flow so a second page could also do it is exactly how two
// divergent copies of a money path start, so the actions link to the dashboard
// that already owns them.
const T = {
  ar: {
    back: 'رجوع', notFound: 'هذا الطلب غير متاح',
    offers: 'العروض', noOffers: 'لا توجد عروض بعد',
    qty: 'الكمية', unit: 'سعر الوحدة', moq: 'أقل كمية', days: 'مدة التسليم',
    shipping: 'الشحن', daysUnit: 'يوم', openDashboard: 'إدارة الطلب في لوحة التحكم', sample: 'العيّنة',
    status: 'الحالة', supplier: 'المورّد',
  },
  en: {
    back: 'Back', notFound: 'This request is not available',
    offers: 'Offers', noOffers: 'No offers yet',
    qty: 'Quantity', unit: 'Unit price', moq: 'MOQ', days: 'Lead time',
    shipping: 'Shipping', daysUnit: 'days', openDashboard: 'Manage this request in your dashboard', sample: 'Sample',
    status: 'Status', supplier: 'Supplier',
  },
  zh: {
    back: '返回', notFound: '此需求不可用',
    offers: '报价', noOffers: '暂无报价',
    qty: '数量', unit: '单价', moq: '起订量', days: '交期',
    shipping: '运费', daysUnit: '天', openDashboard: '在控制面板中管理此需求', sample: '样品',
    status: '状态', supplier: '供应商',
  },
};

export default function RequestDetail({ lang = 'ar', user }) {
  const { id } = useParams();
  const nav = useNavigate();
  const isAr = lang === 'ar';
  const t = T[lang] || T.ar;
  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';

  const [request, setRequest] = useState(null);
  const [offers, setOffers] = useState([]);
  const [sarByOffer, setSarByOffer] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Arriving from a WhatsApp button with no session is the normal case, not an
    // error. Send them to sign in and come back here.
    if (!user) { nav(`/login?next=${encodeURIComponent(`/requests/${id}`)}`, { replace: true }); return undefined; }

    let cancelled = false;
    (async () => {
      setLoading(true);
      // No access checks here on purpose: RLS on requests and offers already
      // decides this, and a second copy in the client would be the one that
      // drifts. Nothing comes back if the viewer has no standing.
      const { data: req } = await sb.from('requests').select('*').eq('id', id).maybeSingle();
      if (cancelled) return;
      if (!req) { setRequest(null); setLoading(false); return; }

      const { data: offerRows } = await sb.from('offers')
        .select('*').eq('request_id', id).order('created_at', { ascending: false });
      const withSuppliers = await attachSupplierProfiles(sb, offerRows || [], 'supplier_id', 'profiles');
      // The request's TRADER reads every amount in SAR only (owner,
      // 2026-09-29), from offer_amounts_sar() — the arithmetic payment_quote()
      // charges from. Suppliers and admins keep the offer's own currency.
      const sar = req.buyer_id === user.id
        ? await fetchOfferAmounts(sb, (offerRows || []).map((o) => o.id))
        : {};
      if (cancelled) return;
      setSarByOffer(sar);
      setRequest(req);
      setOffers(withSuppliers || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, user, nav]);

  if (!user) return null;
  if (loading) return <div className="full-page"><div className="fx-wrap"><BrandedLoading lang={lang} tone="product" /></div></div>;

  if (!request) {
    return (
      <div className="full-page" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="fx-wrap">
          <button className={`fx-back${isAr ? ' ar' : ''}`} onClick={() => nav('/dashboard?tab=requests')}>
            {isAr ? '→ ' : '← '}{t.back}
          </button>
          {/* Deliberately the same message whether the id is unknown or simply
              not this viewer's: distinguishing them confirms a request exists. */}
          <p style={{ color: 'var(--text-secondary)', fontFamily: font }}>{t.notFound}</p>
        </div>
        <Footer lang={lang} />
      </div>
    );
  }

  const unitDef = REQUEST_UNITS.find((u) => u.key === request.unit);
  const unitLabel = unitDef ? labelFor(unitDef, lang) : (request.unit || '');
  const title = (isAr ? request.title_ar : lang === 'zh' ? request.title_zh : request.title_en)
    || request.title_en || request.title_ar || request.title_zh || '—';
  const money = (v, ccy) => (v === null || v === undefined || v === '' ? '—'
    : `${formatPriceLocale(Number(v), lang)} ${ccy || ''}`.trim());
  // The trader sees SAR only; `kind` picks the offer_amounts_sar column. A
  // missing SAR figure is a dash — never the supplier's USD/CNY.
  const isTrader = request.buyer_id === user.id;
  const offerMoney = (o, kind, raw) => {
    if (!isTrader) return money(raw, o.currency);
    const a = sarByOffer[o.id];
    const v = a && a.payable ? a[kind] : null;
    return v == null ? '—' : `${formatSar(v)} ${isAr ? 'ر.س' : 'SAR'}`;
  };

  const cell = { fontSize: 12.5, color: 'var(--text-secondary)', fontFamily: font };
  const label = { fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 3, fontFamily: font };

  return (
    <div className="full-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fx-wrap">
        <button className={`fx-back${isAr ? ' ar' : ''}`} onClick={() => nav('/dashboard?tab=requests')}>
          {isAr ? '→ ' : '← '}{t.back}
        </button>

        <h1 className={`fx-h1${isAr ? ' ar' : ''}`} style={{ fontSize: isAr ? 24 : 28, marginBottom: 6 }}>{title}</h1>
        <p className={`fx-card-meta${isAr ? ' ar' : ''}`} style={{ margin: '0 0 18px', fontSize: 12.5 }}>
          {request.request_ref ? `${request.request_ref} · ` : ''}
          {/* The unit was the RAW key too — «الكمية: 500 carton». Same defect
              as the city below it, same fix: the label comes from the list the
              request form wrote the value from. */}
          {t.qty}: {request.quantity || '—'} {unitLabel}
          {/* Was the RAW column: «الحالة: offers_received». */}
          {request.status ? ` · ${t.status}: ${requestStatusLabel(request.status, lang)}` : ''}
        </p>

        <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 10px', fontFamily: font }}>
          {t.offers} {offers.length > 0 ? `(${offers.length})` : ''}
        </h2>

        {offers.length === 0 ? (
          <p style={{ ...cell, marginBottom: 20 }}>{t.noOffers}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {offers.map((o) => {
              const sup = o.profiles || {};
              const supName = (sup.company_name_latin || '').trim() || sup.company_name || '—';
              return (
                <div key={o.id} style={{
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
                  background: 'var(--bg-subtle)', padding: '13px 15px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                    <div>
                      <div style={label}>{t.supplier}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', fontFamily: font }}>{supName}</div>
                    </div>
                    <div style={{ textAlign: isAr ? 'left' : 'right' }}>
                      <div style={label}>{t.unit}</div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', direction: 'ltr' }}>
                        {offerMoney(o, 'unit_price_sar', o.price)}
                      </div>
                    </div>
                  </div>
                  {/* WHO DELIVERS — the same sentence the dashboard card
                      leads with, from the same source. This page is what the
                      offer email and the WhatsApp button open, so it is often
                      the FIRST place a trader reads a quote; it must not say
                      less about the deal than the dashboard does. */}
                  {(() => {
                    const line = deliveryLine(o.price_basis, lang, { city: request.ship_city });
                    return line ? <p style={{ ...cell, margin: '0 0 8px' }}>{line}</p> : null;
                  })()}
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <span style={cell}>{t.moq}: {o.moq || '—'}</span>
                    {/* FIXED 2026-09-21: was `o.days`, which is not a column on
                        `offers` — the select is `*`, so the key was simply
                        undefined and this row rendered "—" on EVERY offer since
                        the page was written. The column is `delivery_days`. */}
                    <span style={cell}>{t.days}: {o.delivery_days ? `${o.delivery_days} ${t.daysUnit}` : '—'}</span>
                    {o.shipping_cost !== null && o.shipping_cost !== undefined && (
                      <span style={cell}>{t.shipping}: {offerMoney(o, 'shipping_sar', o.shipping_cost)}</span>
                    )}
                    {/* Was the RAW column: «الحالة: pending». Same map the
                        supplier dashboard reads offers.status through. */}
                    {o.status && <span style={cell}>{t.status}: {OFFER_STATUS[lang]?.[o.status] || OFFER_STATUS.ar[o.status] || o.status}</span>}
                  </div>
                  {/* Validity and the sample answer, same as the dashboard.
                      Both were stored and rendered nowhere. */}
                  {(() => {
                    const v = offerValidityText(o, lang);
                    if (!v) return null;
                    return <p style={{ ...cell, margin: '8px 0 0', color: v.expired ? 'var(--red)' : undefined }}>{v.text}</p>;
                  })()}
                  {(() => {
                    const sample = offerSampleText(o, lang, (amt) => offerMoney(o, 'sample_price_sar', amt));
                    if (!sample) return null;
                    return <p style={{ ...cell, margin: '4px 0 0' }}>{t.sample}: {sample}</p>;
                  })()}
                </div>
              );
            })}
          </div>
        )}

        {/* Every action lives in the dashboard. Accepting an offer writes two
            tables, a notification and an email; a second implementation of that
            is a second thing to keep correct. */}
        <button className={`fx-btn-primary${isAr ? ' ar' : ''}`} onClick={() => nav('/dashboard?tab=requests')}>
          {t.openDashboard}
        </button>
      </div>
      <Footer lang={lang} />
    </div>
  );
}
